import React from 'react';

import { type BaseApiError } from '@lumapps/base-api';
import { type UseQueryOptions } from '@lumapps/base-api/react-query';
import { isSpacesFeatureEnabled } from '@lumapps/communities/ducks/selectors';
import { useDispatch, useSelector } from '@lumapps/redux/react';

import { selectSpaceData, selectSpaceId } from '../ducks/selectors';
import { actions as spaceActions } from '../ducks/slice';
import type { SpaceData, SpaceDataApiOptions } from '../types';
import { useSpaceDataQuery } from './useSpaceDataQuery';

/**
 * Options for useSpaceDataFromRedux hook.
 * Combines API options (previewMode) with React Query options (enabled, retry, etc.)
 */
export type UseSpaceDataFromReduxOptions = SpaceDataApiOptions &
    Omit<UseQueryOptions<SpaceData>, 'queryKey' | 'queryFn'> & {
        /**
         * Reset the Redux space slice when this hook unmounts.
         * Set to true for route/page-level hooks that own the space data lifecycle.
         * @default false
         */
        resetOnUnmount?: boolean;
    };

export interface UseSpaceDataFromReduxReturn {
    /** The space data from Redux store */
    spaceData: ReturnType<typeof selectSpaceData>;
    /** Whether the space data is currently being loaded  */
    isLoading: boolean;
    /**
     * Whether the initial load has completed AND the result is synced to Redux for the requested
     * space (or an error occurred)
     */
    hasInit: boolean;
    /** Whether there was an error loading the space */
    hasError: boolean;
    /** HTTP status code from the error response (e.g., 403, 404), if any */
    errorStatus?: number;
}

/**
 * Hook to fetch space data and return it from Redux store.
 *
 * Uses React Query for fetching/caching and syncs the result to Redux for backward compatibility.
 * For new code that doesn't need Redux, prefer `useSpaceDataQuery`.
 *
 * Automatically fetches space data if:
 * - Feature flag is enabled
 * - spaceId is provided
 * - enabled option is not false
 *
 * @param spaceId - The ID of the space to fetch. If not provided, won't fetch anything.
 * @param options - Optional API options (previewMode) and React Query options (enabled, retry, etc.)
 */
export const useSpaceDataFromRedux = (
    spaceId?: string,
    options?: UseSpaceDataFromReduxOptions,
): UseSpaceDataFromReduxReturn => {
    const dispatch = useDispatch();
    const isFeatureEnabled = useSelector(isSpacesFeatureEnabled);
    const spaceData = useSelector(selectSpaceData);
    const syncedSpaceId = useSelector(selectSpaceId);

    // Extract API options from the combined options
    const { previewMode, resetOnUnmount = false, ...queryOptions } = options || {};

    const query = useSpaceDataQuery(spaceId || '', {
        previewMode,
        ...queryOptions,
        // The internal guards (feature flag + valid spaceId) always apply; callers can
        // only narrow further by passing `enabled: false`, never bypass them.
        enabled: (queryOptions.enabled ?? true) && isFeatureEnabled && !!spaceId,
    });

    // Clear Redux state on unmount (if forced)
    React.useEffect(
        () => () => {
            if (resetOnUnmount) {
                dispatch(spaceActions.reset());
            }
        },
        [dispatch, resetOnUnmount],
    );

    // Sync React Query data to Redux when it arrives
    // This maintains backward compatibility for Redux selectors (selectSpaceData, selectSpacePermissions, etc.)
    React.useEffect(() => {
        if (query.isSuccess && query.data && spaceId) {
            dispatch(spaceActions.success({ data: query.data, id: spaceId }));
        }
    }, [query.isSuccess, query.data, spaceId, dispatch]);

    // Extract HTTP status from error response
    const errorStatus = query.isError && query.error ? (query.error as BaseApiError)?.response?.status : undefined;

    // Determine if Redux is synced with the latest successful query for the current spaceId
    const isReduxSynced = query.isSuccess && !!spaceData && syncedSpaceId === spaceId;

    return {
        spaceData,
        isLoading: query.isInitialLoading,
        hasInit: isReduxSynced || query.isError,
        hasError: query.isError,
        errorStatus,
    };
};
